// src/components/admin/page-management/section-forms/StatisticsForm.js
import React, { useState } from 'react';
import { 
  Plus, 
  Trash, 
  MoveUp, 
  MoveDown, 
  BarChart3,
  Layout,
  Palette,
  Type,
  Sparkles,
  Settings,
  ChevronDown,
  ChevronUp,
  GripVertical,
  Hash,
  Tag,
  Clock,
  Eye,
  EyeOff
} from 'lucide-react';
import ColorPicker from '../../shared/ColorPicker';

const StatisticsForm = ({ content, updateContent }) => {
  const [expandedSections, setExpandedSections] = useState({
    statistics: true,
    layout: false,
    background: false,
    cards: false,
    text: false,
    animation: false
  });

  const [draggedIndex, setDraggedIndex] = useState(null);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Handle adding a new statistic
  const handleAddStatistic = () => {
    const statistics = [...(content?.statistics || [])];
    statistics.push({
      value: "100+",
      label: "New Statistic",
      delay: statistics.length * 200
    });
    updateContent('statistics', statistics);
  };

  // Handle removing a statistic
  const handleRemoveStatistic = (index) => {
    const statistics = [...(content?.statistics || [])];
    statistics.splice(index, 1);
    updateContent('statistics', statistics);
  };

  // Handle moving a statistic up
  const handleMoveUp = (index) => {
    if (index === 0) return;
    const statistics = [...(content?.statistics || [])];
    const temp = statistics[index];
    statistics[index] = statistics[index - 1];
    statistics[index - 1] = temp;
    updateContent('statistics', statistics);
  };

  // Handle moving a statistic down
  const handleMoveDown = (index) => {
    const statistics = [...(content?.statistics || [])];
    if (index === statistics.length - 1) return;
    const temp = statistics[index];
    statistics[index] = statistics[index + 1];
    statistics[index + 1] = temp;
    updateContent('statistics', statistics);
  };

  // Handle updating a statistic
  const handleUpdateStatistic = (index, field, value) => {
    const statistics = [...(content?.statistics || [])];
    statistics[index] = {
      ...statistics[index],
      [field]: value
    };
    updateContent('statistics', statistics);
  };

  const SectionHeader = ({ id, title, icon: Icon, isExpanded, description, count }) => (
    <button
      onClick={() => toggleSection(id)}
      className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 rounded-lg border border-blue-200 transition-all duration-200 group"
    >
      <div className="flex items-center space-x-3">
        <div className="p-2 bg-white rounded-lg shadow-sm group-hover:shadow-md transition-shadow">
          <Icon className="w-5 h-5 text-blue-600" />
        </div>
        <div className="text-left">
          <div className="flex items-center space-x-2">
            <h4 className="font-semibold text-gray-800">{title}</h4>
            {count !== undefined && (
              <span className="bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded-full font-medium">
                {count}
              </span>
            )}
          </div>
          {description && (
            <p className="text-sm text-gray-600 mt-1">{description}</p>
          )}
        </div>
      </div>
      <div className="flex items-center space-x-2">
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-blue-600" />
        ) : (
          <ChevronDown className="w-5 h-5 text-blue-600" />
        )}
      </div>
    </button>
  );

  const ToggleSwitch = ({ checked, onChange, label, description }) => (
    <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 hover:border-blue-300 transition-colors">
      <div className="flex-1">
        <label className="block text-sm font-medium text-gray-700">{label}</label>
        {description && (
          <p className="text-xs text-gray-500 mt-1">{description}</p>
        )}
      </div>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
          checked ? 'bg-blue-600' : 'bg-gray-200'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            checked ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );

  const InputGroup = ({ label, description, children, required = false }) => (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      {description && (
        <p className="text-xs text-gray-500">{description}</p>
      )}
      {children}
    </div>
  );

  const TextInput = ({ value, onChange, placeholder, className = "" }) => (
    <input
      type="text"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={`w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${className}`}
    />
  );

  const NumberInput = ({ value, onChange, min, max, step, placeholder, className = "" }) => (
    <input
      type="number"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={`w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${className}`}
    />
  );

  const Select = ({ value, onChange, options, className = "" }) => (
    <select
      value={value}
      onChange={onChange}
      className={`w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white ${className}`}
    >
      {options.map(option => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );

  const ColorPickerGroup = ({ label, color, onChange, description, allowAlpha = false }) => (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      {description && (
        <p className="text-xs text-gray-500">{description}</p>
      )}
      <div className="flex items-center space-x-3 p-3 bg-white rounded-lg border border-gray-200">
        <ColorPicker
          color={color}
          onChange={onChange}
          allowAlpha={allowAlpha}
        />
        <span className="text-sm text-gray-600 font-mono">{color}</span>
      </div>
    </div>
  );

  const StatisticCard = ({ stat, index, totalStats }) => (
    <div className="bg-white border-2 border-gray-100 rounded-xl p-6 hover:border-blue-200 transition-all duration-200 shadow-sm hover:shadow-md">
      {/* Card Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-lg">
            <GripVertical className="w-4 h-4 text-blue-600" />
          </div>
          <div>
            <h5 className="font-semibold text-gray-800">Statistic #{index + 1}</h5>
            <p className="text-xs text-gray-500">Configure your stat display</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-1">
          <button
            type="button"
            onClick={() => handleMoveUp(index)}
            disabled={index === 0}
            className={`p-2 rounded-lg transition-colors ${
              index === 0 
                ? 'text-gray-300 cursor-not-allowed' 
                : 'text-gray-600 hover:bg-gray-100 hover:text-blue-600'
            }`}
            title="Move Up"
          >
            <MoveUp size={16} />
          </button>
          <button
            type="button"
            onClick={() => handleMoveDown(index)}
            disabled={index === totalStats - 1}
            className={`p-2 rounded-lg transition-colors ${
              index === totalStats - 1 
                ? 'text-gray-300 cursor-not-allowed' 
                : 'text-gray-600 hover:bg-gray-100 hover:text-blue-600'
            }`}
            title="Move Down"
          >
            <MoveDown size={16} />
          </button>
          <button
            type="button"
            onClick={() => handleRemoveStatistic(index)}
            className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
            title="Remove Statistic"
          >
            <Trash size={16} />
          </button>
        </div>
      </div>

      {/* Statistic Content */}
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <InputGroup
            label="Value"
            description="The number or metric to display"
            required
          >
            <div className="relative">
              <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <TextInput
                value={stat.value}
                onChange={(e) => handleUpdateStatistic(index, 'value', e.target.value)}
                placeholder="e.g., 1000+, 98%, 24/7"
                className="pl-10"
              />
            </div>
          </InputGroup>
          
          <InputGroup
            label="Label"
            description="Description of what this statistic represents"
            required
          >
            <div className="relative">
              <Tag className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <TextInput
                value={stat.label}
                onChange={(e) => handleUpdateStatistic(index, 'label', e.target.value)}
                placeholder="e.g., Happy Clients"
                className="pl-10"
              />
            </div>
          </InputGroup>
        </div>
        
        <InputGroup
          label="Animation Delay"
          description="Stagger the animation timing (in milliseconds)"
        >
          <div className="relative">
            <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <NumberInput
              value={stat.delay}
              onChange={(e) => handleUpdateStatistic(index, 'delay', parseInt(e.target.value, 10))}
              min={0}
              step={100}
              placeholder="200"
              className="pl-10"
            />
          </div>
        </InputGroup>
      </div>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto space-y-6 p-6 bg-gray-50 rounded-xl">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center space-x-3 p-4 bg-white rounded-xl shadow-sm border border-gray-200">
          <BarChart3 className="w-8 h-8 text-blue-600" />
          <div>
            <h3 className="text-2xl font-bold text-gray-800">Statistics Section</h3>
            <p className="text-gray-600">Showcase impressive numbers and achievements</p>
          </div>
        </div>
      </div>

      {/* Statistics Management */}
      <div className="space-y-4">
        <SectionHeader 
          id="statistics"
          title="Your Statistics"
          icon={BarChart3}
          isExpanded={expandedSections.statistics}
          description="Add and manage the statistics you want to display"
          count={content?.statistics?.length || 0}
        />
        
        {expandedSections.statistics && (
          <div className="bg-white p-6 rounded-xl border border-gray-200 space-y-6">
            {/* Add Button */}
            <div className="flex justify-center">
              <button
                type="button"
                onClick={handleAddStatistic}
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
              >
                <Plus size={20} className="mr-2" />
                Add New Statistic
              </button>
            </div>
            
            {/* Statistics List */}
            <div className="space-y-6">
              {(content?.statistics || []).map((stat, index) => (
                <StatisticCard 
                  key={index}
                  stat={stat}
                  index={index}
                  totalStats={(content?.statistics || []).length}
                />
              ))}
              
              {(content?.statistics || []).length === 0 && (
                <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                  <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h4 className="text-lg font-medium text-gray-600 mb-2">No statistics yet</h4>
                  <p className="text-gray-500 mb-4">Start by adding your first impressive statistic!</p>
                  <button
                    type="button"
                    onClick={handleAddStatistic}
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Plus size={16} className="mr-2" />
                    Add First Statistic
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Layout Settings */}
      <div className="space-y-4">
        <SectionHeader 
          id="layout"
          title="Layout & Spacing"
          icon={Layout}
          isExpanded={expandedSections.layout}
          description="Control how statistics are arranged and spaced"
        />
        
        {expandedSections.layout && (
          <div className="bg-white p-6 rounded-xl border border-gray-200 space-y-6">
            <InputGroup
              label="Container Width"
              description="Maximum width of the statistics section"
            >
              <Select
                value={content?.containerMaxWidth || 'max-w-7xl'}
                onChange={(e) => updateContent('containerMaxWidth', e.target.value)}
                options={[
                  { value: 'max-w-5xl', label: 'Narrow (Best for mobile-first)' },
                  { value: 'max-w-6xl', label: 'Standard (Balanced)' },
                  { value: 'max-w-7xl', label: ' Wide (Desktop-focused)' },
                  { value: '', label: '↔Full Width (Edge to edge)' }
                ]}
              />
            </InputGroup>

            <div className="border-t pt-6">
              <h5 className="font-medium text-gray-800 mb-4">Responsive Columns</h5>
              <p className="text-sm text-gray-600 mb-4">Set how many statistics show per row on different devices</p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <InputGroup label=" Mobile">
                  <Select
                    value={content?.statsPerRow?.mobile || 1}
                    onChange={(e) => updateContent('statsPerRow', {
                      ...content?.statsPerRow,
                      mobile: parseInt(e.target.value)
                    })}
                    options={[
                      { value: 1, label: '1 Column' },
                      { value: 2, label: '2 Columns' }
                    ]}
                  />
                </InputGroup>
                
                <InputGroup label="Tablet">
                  <Select
                    value={content?.statsPerRow?.tablet || 2}
                    onChange={(e) => updateContent('statsPerRow', {
                      ...content?.statsPerRow,
                      tablet: parseInt(e.target.value)
                    })}
                    options={[
                      { value: 1, label: '1 Column' },
                      { value: 2, label: '2 Columns' },
                      { value: 3, label: '3 Columns' }
                    ]}
                  />
                </InputGroup>
                
                <InputGroup label="Desktop">
                  <Select
                    value={content?.statsPerRow?.desktop || 4}
                    onChange={(e) => updateContent('statsPerRow', {
                      ...content?.statsPerRow,
                      desktop: parseInt(e.target.value)
                    })}
                    options={[
                      { value: 2, label: '2 Columns' },
                      { value: 3, label: '3 Columns' },
                      { value: 4, label: '4 Columns' },
                      { value: 5, label: '5 Columns' }
                    ]}
                  />
                </InputGroup>
              </div>
            </div>

            <div className="border-t pt-6">
              <h5 className="font-medium text-gray-800 mb-4">Section Spacing</h5>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <InputGroup
                  label="Gap Between Cards"
                  description="Space between individual statistics"
                >
                  <TextInput
                    value={content?.cardGap || '2rem'}
                    onChange={(e) => updateContent('cardGap', e.target.value)}
                    placeholder="2rem"
                  />
                </InputGroup>
                
                <InputGroup
                  label="Section Bottom Margin"
                  description="Space below the entire section"
                >
                  <TextInput
                    value={content?.marginBottom || '5rem'}
                    onChange={(e) => updateContent('marginBottom', e.target.value)}
                    placeholder="5rem"
                  />
                </InputGroup>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <InputGroup
                  label="Top Padding"
                  description="Inner space at the top"
                >
                  <TextInput
                    value={content?.paddingTop || '8rem'}
                    onChange={(e) => updateContent('paddingTop', e.target.value)}
                    placeholder="8rem"
                  />
                </InputGroup>
                
                <InputGroup
                  label="Bottom Padding"
                  description="Inner space at the bottom"
                >
                  <TextInput
                    value={content?.paddingBottom || '10rem'}
                    onChange={(e) => updateContent('paddingBottom', e.target.value)}
                    placeholder="10rem"
                  />
                </InputGroup>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Background Settings */}
      <div className="space-y-4">
        <SectionHeader 
          id="background"
          title="Background & Visual Effects"
          icon={Palette}
          isExpanded={expandedSections.background}
          description="Customize the section background and decorative elements"
        />
        
        {expandedSections.background && (
          <div className="bg-white p-6 rounded-xl border border-gray-200 space-y-6">
            <ColorPickerGroup
              label="Main Background Color"
              color={content?.backgroundColor || '#033568'}
              onChange={(color) => updateContent('backgroundColor', color)}
              description="Primary background color for the statistics section"
            />

            <div className="border-t pt-6">
              <ToggleSwitch
                checked={content?.backgroundOverlayEnabled !== false}
                onChange={(checked) => updateContent('backgroundOverlayEnabled', checked)}
                label="Gradient Overlay"
                description="Add a subtle gradient over the background"
              />
              
              {content?.backgroundOverlayEnabled !== false && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4 pl-4 border-l-2 border-purple-200">
                  <ColorPickerGroup
                    label="Gradient Start"
                    color={content?.overlayFrom || 'rgba(31, 41, 55, 0.3)'}
                    onChange={(color) => updateContent('overlayFrom', color)}
                    allowAlpha={true}
                  />
                  
                  <ColorPickerGroup
                    label="Gradient End"
                    color={content?.overlayTo || 'rgba(30, 58, 138, 0.3)'}
                    onChange={(color) => updateContent('overlayTo', color)}
                    allowAlpha={true}
                  />
                </div>
              )}
            </div>

            <div className="border-t pt-6">
              <ToggleSwitch
                checked={content?.showFloatingOrbs !== false}
                onChange={(checked) => updateContent('showFloatingOrbs', checked)}
                label="Floating Orbs"
                description="Add animated floating orbs for visual interest"
              />
              
              {content?.showFloatingOrbs !== false && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4 pl-4 border-l-2 border-yellow-200">
                  <ColorPickerGroup
                    label="First Orb Color"
                    color={content?.orbColors?.[0] || '#FFEB3B'}
                    onChange={(color) => updateContent('orbColors', [
                      color,
                      content?.orbColors?.[1] || '#4299E1'
                    ])}
                  />
                  
                  <ColorPickerGroup
                    label="Second Orb Color"
                    color={content?.orbColors?.[1] || '#4299E1'}
                    onChange={(color) => updateContent('orbColors', [
                      content?.orbColors?.[0] || '#FFEB3B',
                      color
                    ])}
                  />
                </div>
              )}
            </div>

            <div className="border-t pt-6">
              <ToggleSwitch
                checked={content?.showSeparatorLines !== false}
                onChange={(checked) => updateContent('showSeparatorLines', checked)}
                label="Decorative Lines"
                description="Add elegant separator lines above and below"
              />
              
              {content?.showSeparatorLines !== false && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4 pl-4 border-l-2 border-indigo-200">
                  <ColorPickerGroup
                    label="Top Line Color"
                    color={content?.separatorColorTop || 'rgba(1,50,99,0.8)'}
                    onChange={(color) => updateContent('separatorColorTop', color)}
                    allowAlpha={true}
                  />
                  
                  <ColorPickerGroup
                    label="Bottom Line Color"
                    color={content?.separatorColorBottom || 'rgba(1,50,99,0.8)'}
                    onChange={(color) => updateContent('separatorColorBottom', color)}
                    allowAlpha={true}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Card Appearance */}
      <div className="space-y-4">
        <SectionHeader 
          id="cards"
          title="Card Design"
          icon={Eye}
          isExpanded={expandedSections.cards}
          description="Customize the appearance of individual statistic cards"
        />
        
        {expandedSections.cards && (
          <div className="bg-white p-6 rounded-xl border border-gray-200 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <ColorPickerGroup
                label="Card Background"
                color={content?.cardBackgroundColor || 'rgba(255, 255, 255, 0.05)'}
                onChange={(color) => updateContent('cardBackgroundColor', color)}
                allowAlpha={true}
                description="Background color for individual cards"
              />
              
              <ColorPickerGroup
                label="Card Border"
                color={content?.cardBorderColor || 'rgba(255, 255, 255, 0.1)'}
                onChange={(color) => updateContent('cardBorderColor', color)}
                allowAlpha={true}
                description="Border color around cards"
              />
            </div>

            <InputGroup
              label="Corner Roundness"
              description="How rounded the card corners should be"
            >
              <TextInput
                value={content?.borderRadius || '0.5rem'}
                onChange={(e) => updateContent('borderRadius', e.target.value)}
                placeholder="0.5rem"
              />
            </InputGroup>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <ToggleSwitch
                checked={content?.shadowEnabled !== false}
                onChange={(checked) => updateContent('shadowEnabled', checked)}
                label="Card Shadow"
                description="Add subtle shadows behind cards"
              />
              
              <ToggleSwitch
                checked={content?.cardHoverEffect !== false}
                onChange={(checked) => updateContent('cardHoverEffect', checked)}
                label="Hover Animation"
                description="Cards lift slightly when hovered"
              />
            </div>
          </div>
        )}
      </div>

      {/* Text Appearance */}
      <div className="space-y-4">
        <SectionHeader 
          id="text"
          title="Typography"
          icon={Type}
          isExpanded={expandedSections.text}
          description="Control text colors and sizes"
        />
        
        {expandedSections.text && (
          <div className="bg-white p-6 rounded-xl border border-gray-200 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <ColorPickerGroup
                label="Number Color"
                color={content?.textColor || '#ffffff'}
                onChange={(color) => updateContent('textColor', color)}
                description="Color for the statistic values"
              />
              
              <ColorPickerGroup
                label="Label Color"
                color={content?.statLabelColor || '#d1d5db'}
                onChange={(color) => updateContent('statLabelColor', color)}
                description="Color for the descriptive labels"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InputGroup
                label="Number Size"
                description="How large the statistic numbers appear"
              >
                <Select
                  value={content?.statValueSize || 'text-4xl md:text-5xl'}
                  onChange={(e) => updateContent('statValueSize', e.target.value)}
                  options={[
                    { value: 'text-3xl md:text-4xl', label: 'Small' },
                    { value: 'text-4xl md:text-5xl', label: 'Medium (Recommended)' },
                    { value: 'text-5xl md:text-6xl', label: 'Large' }
                  ]}
                />
              </InputGroup>
              
              <InputGroup
                label="Label Size"
                description="How large the descriptive labels appear"
              >
                <Select
                  value={content?.statLabelSize || 'text-lg'}
                  onChange={(e) => updateContent('statLabelSize', e.target.value)}
                  options={[
                    { value: 'text-base', label: 'Small' },
                    { value: 'text-lg', label: 'Medium (Recommended)' },
                    { value: 'text-xl', label: 'Large' }
                  ]}
                />
              </InputGroup>
            </div>
          </div>
        )}
      </div>

      {/* Animation Settings */}
      <div className="space-y-4">
        <SectionHeader 
          id="animation"
          title="Animations"
          icon={Sparkles}
          isExpanded={expandedSections.animation}
          description="Add smooth animations when statistics come into view"
        />
        
        {expandedSections.animation && (
          <div className="bg-white p-6 rounded-xl border border-gray-200 space-y-6">
            <ToggleSwitch
              checked={content?.animationEnabled !== false}
              onChange={(checked) => updateContent('animationEnabled', checked)}
              label="Enable Animations"
              description="Statistics will animate into view when users scroll to them"
            />
            
            {content?.animationEnabled !== false && (
              <div className="pl-4 border-l-2 border-green-200">
                <InputGroup
                  label="Animation Style"
                  description="How the statistics should appear"
                >
                  <Select
                    value={content?.animationStyle || 'fade'}
                    onChange={(e) => updateContent('animationStyle', e.target.value)}
                    options={[
                      { value: 'fade', label: 'Fade In (Subtle)' },
                      { value: 'slide', label: 'Slide In (Dynamic)' },
                      { value: 'scale', label: 'Scale Up (Bold)' }
                    ]}
                  />
                </InputGroup>
              </div>
            )}
          </div>
        )}
      </div>
      
    
    </div>
  );
};

export default StatisticsForm;



// // src/components/admin/page-management/section-forms/StatisticsForm.js
// import React from 'react';
// import { Plus, Trash, MoveUp, MoveDown } from 'lucide-react';
// import ColorPicker from '../../shared/ColorPicker';

// const StatisticsForm = ({ content, updateContent }) => {
//   // Handle adding a new statistic
//   const handleAddStatistic = () => {
//     const statistics = [...(content?.statistics || [])];
//     statistics.push({
//       value: "100+",
//       label: "New Statistic",
//       delay: statistics.length * 200
//     });
//     updateContent('statistics', statistics);
//   };

//   // Handle removing a statistic
//   const handleRemoveStatistic = (index) => {
//     const statistics = [...(content?.statistics || [])];
//     statistics.splice(index, 1);
//     updateContent('statistics', statistics);
//   };

//   // Handle moving a statistic up
//   const handleMoveUp = (index) => {
//     if (index === 0) return;
//     const statistics = [...(content?.statistics || [])];
//     const temp = statistics[index];
//     statistics[index] = statistics[index - 1];
//     statistics[index - 1] = temp;
//     updateContent('statistics', statistics);
//   };

//   // Handle moving a statistic down
//   const handleMoveDown = (index) => {
//     const statistics = [...(content?.statistics || [])];
//     if (index === statistics.length - 1) return;
//     const temp = statistics[index];
//     statistics[index] = statistics[index + 1];
//     statistics[index + 1] = temp;
//     updateContent('statistics', statistics);
//   };

//   // Handle updating a statistic
//   const handleUpdateStatistic = (index, field, value) => {
//     const statistics = [...(content?.statistics || [])];
//     statistics[index] = {
//       ...statistics[index],
//       [field]: value
//     };
//     updateContent('statistics', statistics);
//   };

//   return (
//     <div className="space-y-6">
//       <h3 className="text-lg font-semibold">Statistics Section</h3>
      
//       {/* Statistics Items */}
//       <div className="bg-gray-50 p-4 rounded-md">
//         <div className="flex justify-between items-center mb-4">
//           <h4 className="font-medium">Statistics</h4>
//           <button
//             type="button"
//             onClick={handleAddStatistic}
//             className="px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center"
//           >
//             <Plus size={16} className="mr-1" /> Add Statistic
//           </button>
//         </div>
        
//         {(content?.statistics || []).map((stat, index) => (
//           <div 
//             key={index} 
//             className="mb-6 p-4 border border-gray-200 rounded bg-white"
//           >
//             <div className="flex justify-between items-center mb-2">
//               <h5 className="font-medium">Statistic #{index + 1}</h5>
//               <div className="flex space-x-1">
//                 <button
//                   type="button"
//                   onClick={() => handleMoveUp(index)}
//                   disabled={index === 0}
//                   className={`p-1 rounded ${index === 0 ? 'text-gray-300' : 'text-gray-600 hover:bg-gray-100'}`}
//                   title="Move Up"
//                 >
//                   <MoveUp size={16} />
//                 </button>
//                 <button
//                   type="button"
//                   onClick={() => handleMoveDown(index)}
//                   disabled={index === (content?.statistics || []).length - 1}
//                   className={`p-1 rounded ${index === (content?.statistics || []).length - 1 ? 'text-gray-300' : 'text-gray-600 hover:bg-gray-100'}`}
//                   title="Move Down"
//                 >
//                   <MoveDown size={16} />
//                 </button>
//                 <button
//                   type="button"
//                   onClick={() => handleRemoveStatistic(index)}
//                   className="p-1 text-red-500 hover:bg-red-50 rounded"
//                   title="Remove"
//                 >
//                   <Trash size={16} />
//                 </button>
//               </div>
//             </div>
            
//             <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
//               <div>
//                 <label className="block text-sm font-medium mb-1">Value</label>
//                 <input
//                   type="text"
//                   value={stat.value}
//                   onChange={(e) => handleUpdateStatistic(index, 'value', e.target.value)}
//                   className="w-full p-2 border rounded"
//                   placeholder="e.g., 1000+, 98%, 24/7"
//                 />
//               </div>
//               <div>
//                 <label className="block text-sm font-medium mb-1">Label</label>
//                 <input
//                   type="text"
//                   value={stat.label}
//                   onChange={(e) => handleUpdateStatistic(index, 'label', e.target.value)}
//                   className="w-full p-2 border rounded"
//                   placeholder="e.g., Happy Clients"
//                 />
//               </div>
//             </div>
            
//             <div>
//               <label className="block text-sm font-medium mb-1">Animation Delay (ms)</label>
//               <input
//                 type="number"
//                 min="0"
//                 step="100"
//                 value={stat.delay}
//                 onChange={(e) => handleUpdateStatistic(index, 'delay', parseInt(e.target.value, 10))}
//                 className="w-32 p-2 border rounded"
//               />
//               <p className="text-xs text-gray-500 mt-1">
//                 Only applies when animations are enabled.
//               </p>
//             </div>
//           </div>
//         ))}
        
//         {(content?.statistics || []).length === 0 && (
//           <p className="text-center py-4 text-gray-500">
//             No statistics added. Click the button above to add one.
//           </p>
//         )}
//       </div>
      
//       {/* Layout Settings */}
//       <div className="bg-gray-50 p-4 rounded-md">
//         <h4 className="font-medium mb-4">Layout Settings</h4>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Container Max Width</label>
//           <select
//             value={content?.containerMaxWidth || 'max-w-7xl'}
//             onChange={(e) => updateContent('containerMaxWidth', e.target.value)}
//             className="w-full p-2 border rounded"
//           >
//             <option value="max-w-5xl">Narrow (max-w-5xl)</option>
//             <option value="max-w-6xl">Standard (max-w-6xl)</option>
//             <option value="max-w-7xl">Wide (max-w-7xl)</option>
//             <option value="">Full Width</option>
//           </select>
//         </div>
        
//         <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
//           <div>
//             <label className="block text-sm font-medium mb-1">Mobile Columns</label>
//             <select
//               value={content?.statsPerRow?.mobile || 1}
//               onChange={(e) => updateContent('statsPerRow', {
//                 ...content?.statsPerRow,
//                 mobile: parseInt(e.target.value)
//               })}
//               className="w-full p-2 border rounded"
//             >
//               <option value={1}>1 Column</option>
//               <option value={2}>2 Columns</option>
//             </select>
//           </div>
//           <div>
//             <label className="block text-sm font-medium mb-1">Tablet Columns</label>
//             <select
//               value={content?.statsPerRow?.tablet || 2}
//               onChange={(e) => updateContent('statsPerRow', {
//                 ...content?.statsPerRow,
//                 tablet: parseInt(e.target.value)
//               })}
//               className="w-full p-2 border rounded"
//             >
//               <option value={1}>1 Column</option>
//               <option value={2}>2 Columns</option>
//               <option value={3}>3 Columns</option>
//             </select>
//           </div>
//           <div>
//             <label className="block text-sm font-medium mb-1">Desktop Columns</label>
//             <select
//               value={content?.statsPerRow?.desktop || 4}
//               onChange={(e) => updateContent('statsPerRow', {
//                 ...content?.statsPerRow,
//                 desktop: parseInt(e.target.value)
//               })}
//               className="w-full p-2 border rounded"
//             >
//               <option value={2}>2 Columns</option>
//               <option value={3}>3 Columns</option>
//               <option value={4}>4 Columns</option>
//               <option value={5}>5 Columns</option>
//             </select>
//           </div>
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Card Gap</label>
//           <input
//             type="text"
//             value={content?.cardGap || '2rem'}
//             onChange={(e) => updateContent('cardGap', e.target.value)}
//             className="w-full p-2 border rounded"
//             placeholder="e.g., 2rem, 32px"
//           />
//         </div>
        
//         <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
//           <div>
//             <label className="block text-sm font-medium mb-1">Padding Top</label>
//             <input
//               type="text"
//               value={content?.paddingTop || '8rem'}
//               onChange={(e) => updateContent('paddingTop', e.target.value)}
//               className="w-full p-2 border rounded"
//               placeholder="e.g., 8rem, 128px"
//             />
//           </div>
//           <div>
//             <label className="block text-sm font-medium mb-1">Padding Bottom</label>
//             <input
//               type="text"
//               value={content?.paddingBottom || '10rem'}
//               onChange={(e) => updateContent('paddingBottom', e.target.value)}
//               className="w-full p-2 border rounded"
//               placeholder="e.g., 10rem, 160px"
//             />
//           </div>
//           <div>
//             <label className="block text-sm font-medium mb-1">Margin Bottom</label>
//             <input
//               type="text"
//               value={content?.marginBottom || '5rem'}
//               onChange={(e) => updateContent('marginBottom', e.target.value)}
//               className="w-full p-2 border rounded"
//               placeholder="e.g., 5rem, 80px"
//             />
//           </div>
//         </div>
//       </div>
      
//       {/* Background Settings */}
//       <div className="bg-gray-50 p-4 rounded-md">
//         <h4 className="font-medium mb-4">Background Settings</h4>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Background Color</label>
//           <ColorPicker
//             color={content?.backgroundColor || '#033568'}
//             onChange={(color) => updateContent('backgroundColor', color)}
//           />
//         </div>
        
//         <div className="mb-4">
//           <label className="flex items-center">
//             <input
//               type="checkbox"
//               checked={content?.backgroundOverlayEnabled !== false}
//               onChange={(e) => updateContent('backgroundOverlayEnabled', e.target.checked)}
//               className="mr-2"
//             />
//             <span className="text-sm">Enable Gradient Overlay</span>
//           </label>
//         </div>
        
//         {content?.backgroundOverlayEnabled !== false && (
//           <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
//             <div>
//               <label className="block text-sm font-medium mb-1">Gradient Start Color</label>
//               <ColorPicker
//                 color={content?.overlayFrom || 'rgba(31, 41, 55, 0.3)'}
//                 onChange={(color) => updateContent('overlayFrom', color)}
//                 allowAlpha={true}
//               />
//             </div>
//             <div>
//               <label className="block text-sm font-medium mb-1">Gradient End Color</label>
//               <ColorPicker
//                 color={content?.overlayTo || 'rgba(30, 58, 138, 0.3)'}
//                 onChange={(color) => updateContent('overlayTo', color)}
//                 allowAlpha={true}
//               />
//             </div>
//           </div>
//         )}
        
//         <div className="mb-4">
//           <label className="flex items-center">
//             <input
//               type="checkbox"
//               checked={content?.showFloatingOrbs !== false}
//               onChange={(e) => updateContent('showFloatingOrbs', e.target.checked)}
//               className="mr-2"
//             />
//             <span className="text-sm">Show Floating Orbs</span>
//           </label>
//         </div>
        
//         {content?.showFloatingOrbs !== false && (
//           <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
//             <div>
//               <label className="block text-sm font-medium mb-1">Orb 1 Color</label>
//               <ColorPicker
//                 color={content?.orbColors?.[0] || '#FFEB3B'}
//                 onChange={(color) => updateContent('orbColors', [
//                   color,
//                   content?.orbColors?.[1] || '#4299E1'
//                 ])}
//               />
//             </div>
//             <div>
//               <label className="block text-sm font-medium mb-1">Orb 2 Color</label>
//               <ColorPicker
//                 color={content?.orbColors?.[1] || '#4299E1'}
//                 onChange={(color) => updateContent('orbColors', [
//                   content?.orbColors?.[0] || '#FFEB3B',
//                   color
//                 ])}
//               />
//             </div>
//           </div>
//         )}
        
//         <div className="mb-4">
//           <label className="flex items-center">
//             <input
//               type="checkbox"
//               checked={content?.showSeparatorLines !== false}
//               onChange={(e) => updateContent('showSeparatorLines', e.target.checked)}
//               className="mr-2"
//             />
//             <span className="text-sm">Show Separator Lines</span>
//           </label>
//         </div>
        
//         {content?.showSeparatorLines !== false && (
//           <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
//             <div>
//               <label className="block text-sm font-medium mb-1">Top Separator Color</label>
//               <ColorPicker
//                 color={content?.separatorColorTop || 'rgba(1,50,99,0.8)'}
//                 onChange={(color) => updateContent('separatorColorTop', color)}
//                 allowAlpha={true}
//               />
//             </div>
//             <div>
//               <label className="block text-sm font-medium mb-1">Bottom Separator Color</label>
//               <ColorPicker
//                 color={content?.separatorColorBottom || 'rgba(1,50,99,0.8)'}
//                 onChange={(color) => updateContent('separatorColorBottom', color)}
//                 allowAlpha={true}
//               />
//             </div>
//           </div>
//         )}
//       </div>
      
//       {/* Card Appearance */}
//       <div className="bg-gray-50 p-4 rounded-md">
//         <h4 className="font-medium mb-4">Card Appearance</h4>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Card Background Color</label>
//           <ColorPicker
//             color={content?.cardBackgroundColor || 'rgba(255, 255, 255, 0.05)'}
//             onChange={(color) => updateContent('cardBackgroundColor', color)}
//             allowAlpha={true}
//           />
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Card Border Color</label>
//           <ColorPicker
//             color={content?.cardBorderColor || 'rgba(255, 255, 255, 0.1)'}
//             onChange={(color) => updateContent('cardBorderColor', color)}
//             allowAlpha={true}
//           />
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Border Radius</label>
//           <input
//             type="text"
//             value={content?.borderRadius || '0.5rem'}
//             onChange={(e) => updateContent('borderRadius', e.target.value)}
//             className="w-full p-2 border rounded"
//             placeholder="e.g., 0.5rem, 8px"
//           />
//         </div>
        
//         <div className="mb-4">
//           <label className="flex items-center">
//             <input
//               type="checkbox"
//               checked={content?.shadowEnabled !== false}
//               onChange={(e) => updateContent('shadowEnabled', e.target.checked)}
//               className="mr-2"
//             />
//             <span className="text-sm">Show Card Shadow</span>
//           </label>
//         </div>
        
//         <div className="mb-4">
//           <label className="flex items-center">
//             <input
//               type="checkbox"
//               checked={content?.cardHoverEffect !== false}
//               onChange={(e) => updateContent('cardHoverEffect', e.target.checked)}
//               className="mr-2"
//             />
//             <span className="text-sm">Enable Card Hover Effect</span>
//           </label>
//         </div>
//       </div>
      
//       {/* Text Appearance */}
//       <div className="bg-gray-50 p-4 rounded-md">
//         <h4 className="font-medium mb-4">Text Appearance</h4>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Value Text Color</label>
//           <ColorPicker
//             color={content?.textColor || '#ffffff'}
//             onChange={(color) => updateContent('textColor', color)}
//           />
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Label Text Color</label>
//           <ColorPicker
//             color={content?.statLabelColor || '#d1d5db'}
//             onChange={(color) => updateContent('statLabelColor', color)}
//           />
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Value Text Size</label>
//           <select
//             value={content?.statValueSize || 'text-4xl md:text-5xl'}
//             onChange={(e) => updateContent('statValueSize', e.target.value)}
//             className="w-full p-2 border rounded"
//           >
//             <option value="text-3xl md:text-4xl">Small</option>
//             <option value="text-4xl md:text-5xl">Medium</option>
//             <option value="text-5xl md:text-6xl">Large</option>
//           </select>
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Label Text Size</label>
//           <select
//             value={content?.statLabelSize || 'text-lg'}
//             onChange={(e) => updateContent('statLabelSize', e.target.value)}
//             className="w-full p-2 border rounded"
//           >
//             <option value="text-base">Small</option>
//             <option value="text-lg">Medium</option>
//             <option value="text-xl">Large</option>
//           </select>
//         </div>
//       </div>
      
//       {/* Animation Settings */}
//       <div className="bg-gray-50 p-4 rounded-md">
//         <h4 className="font-medium mb-4">Animation Settings</h4>
        
//         <div className="mb-4">
//           <label className="flex items-center">
//             <input
//               type="checkbox"
//               checked={content?.animationEnabled !== false}
//               onChange={(e) => updateContent('animationEnabled', e.target.checked)}
//               className="mr-2"
//             />
//             <span className="text-sm">Enable Animations</span>
//           </label>
//         </div>
        
//         {content?.animationEnabled !== false && (
//           <div className="mb-4">
//             <label className="block text-sm font-medium mb-1">Animation Style</label>
//             <select
//               value={content?.animationStyle || 'fade'}
//               onChange={(e) => updateContent('animationStyle', e.target.value)}
//               className="w-full p-2 border rounded"
//             >
//               <option value="fade">Fade</option>
//               <option value="slide">Slide</option>
//               <option value="scale">Scale</option>
//             </select>
//           </div>
//         )}
//       </div>
//     </div>
//   );
// };

// export default StatisticsForm;